import { useState } from 'react';
import type { ChangeEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CsvContactsPreviewResponse } from '@cockpit/shared';
import { api, ApiError } from '../lib/api';
import { Button } from '../components/ui/Button';
import { readFileAsText } from '../lib/csv';
import { LABEL_TYPE_CLIENT } from '../lib/format';

export function ContactsImportPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [preview, setPreview] = useState<CsvContactsPreviewResponse | null>(null);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const previewMut = useMutation({
    mutationFn: api.contacts.csvPreview,
    onSuccess: (res) => {
      setPreview(res);
      const ok = new Set<number>();
      for (const r of res.rows) {
        if (r.status === 'ok') ok.add(r.ligne);
      }
      setChecked(ok);
      setError(null);
      setSuccess(null);
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Erreur preview CSV.');
      setPreview(null);
    },
  });

  const commitMut = useMutation({
    mutationFn: api.contacts.csvCommit,
    onSuccess: (res) => {
      setSuccess(`${String(res.contacts_crees)} contact(s) importé(s).`);
      setPreview(null);
      setChecked(new Set());
      void queryClient.invalidateQueries({ queryKey: ['contacts'] });
    },
    onError: (err: unknown) => {
      setError(err instanceof ApiError ? err.message : 'Erreur import CSV.');
    },
  });

  const handleFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file === undefined) return;
    setError(null);
    setSuccess(null);
    readFileAsText(file)
      .then((text) => {
        previewMut.mutate(text);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Erreur lecture');
      });
  };

  const toggle = (ligne: number): void => {
    const next = new Set(checked);
    if (next.has(ligne)) next.delete(ligne);
    else next.add(ligne);
    setChecked(next);
  };

  const handleCommit = (): void => {
    if (preview === null) return;
    const rows = preview.rows.filter((r) => checked.has(r.ligne) && r.status === 'ok');
    commitMut.mutate({ rows });
  };

  const counts =
    preview === null
      ? { ok: 0, doublon: 0, erreur: 0 }
      : preview.rows.reduce(
          (acc, r) => {
            acc[r.status] += 1;
            return acc;
          },
          { ok: 0, doublon: 0, erreur: 0 } as Record<'ok' | 'doublon' | 'erreur', number>,
        );

  return (
    <div className="max-w-5xl flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-bold text-slate-100">Importer contacts</h1>
        <p className="text-xs text-slate-500 mt-1">Upload CSV pour création de contacts en lot.</p>
      </header>

      <section className="bg-slate-900/40 border border-slate-800 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-slate-200">Fichier CSV</h2>
        <p className="text-xs text-slate-500 mt-1">
          Colonnes obligatoires : <code className="text-slate-300">nom, type_client</code>.
        </p>
        <p className="text-xs text-slate-500 mt-1">
          Colonnes optionnelles :{' '}
          <code className="text-slate-300">
            prenom, societe, email, telephone, code_postal, ville, territoire, source,
            statut_pipeline, notes
          </code>
          . Encodage UTF-8, séparateur , ou ; auto-détecté.
        </p>
        <p className="text-xs text-slate-500 mt-1">
          Détection de doublons : email exact ou (société + nom).
        </p>
        <div className="mt-4 flex items-center gap-3">
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={handleFile}
            className="text-xs text-slate-400"
          />
          {previewMut.isPending && <span className="text-xs text-slate-500">Analyse…</span>}
        </div>
      </section>

      {error !== null && (
        <div className="text-xs text-red-300 bg-red-900/20 border border-red-900/50 rounded-md px-3 py-2">
          {error}
        </div>
      )}
      {success !== null && (
        <div className="text-xs text-emerald-300 bg-emerald-900/20 border border-emerald-900/50 rounded-md px-3 py-2">
          {success}
        </div>
      )}

      {preview !== null && (
        <section className="bg-slate-900/40 border border-slate-800 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-200">
              Prévisualisation — {preview.rows.length} lignes (séparateur "
              {preview.separateur_detecte}")
            </h2>
            <div className="text-xs text-slate-400 flex gap-3">
              <span className="text-emerald-400">{counts.ok} OK</span>
              <span className="text-amber-400">{counts.doublon} doublons</span>
              <span className="text-red-400">{counts.erreur} erreurs</span>
            </div>
          </div>

          <div className="max-h-[480px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-900 text-xs text-slate-400 uppercase tracking-wider sticky top-0">
                <tr>
                  <th className="px-3 py-2">✓</th>
                  <th className="px-3 py-2 text-left">L</th>
                  <th className="px-3 py-2 text-left">Nom</th>
                  <th className="px-3 py-2 text-left">Société</th>
                  <th className="px-3 py-2 text-left">Email</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Statut</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {preview.rows.map((r) => (
                  <tr
                    key={r.ligne}
                    className={
                      r.status === 'erreur'
                        ? 'bg-red-950/20'
                        : r.status === 'doublon'
                          ? 'bg-amber-950/20'
                          : ''
                    }
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={checked.has(r.ligne)}
                        disabled={r.status !== 'ok'}
                        onChange={(): void => toggle(r.ligne)}
                      />
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{r.ligne}</td>
                    <td className="px-3 py-2 text-xs text-slate-200">{r.donnees?.nom ?? '—'}</td>
                    <td className="px-3 py-2 text-xs text-slate-300">
                      {r.donnees?.societe ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-300">{r.donnees?.email ?? '—'}</td>
                    <td className="px-3 py-2 text-xs text-slate-400">
                      {r.donnees !== null ? (LABEL_TYPE_CLIENT[r.donnees.type_client] ?? '—') : '—'}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {r.status === 'ok' && <span className="text-emerald-400">OK</span>}
                      {r.status === 'doublon' && (
                        <span
                          className="text-amber-400"
                          title={`Doublon contact #${String(r.contact_existant_id ?? 0)}`}
                        >
                          Doublon
                        </span>
                      )}
                      {r.status === 'erreur' && (
                        <span className="text-red-400" title={r.erreurs.join(' ; ')}>
                          {r.erreurs[0] ?? 'Erreur'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="px-5 py-3 border-t border-slate-800 flex items-center justify-end gap-3">
            <span className="text-xs text-slate-400">{checked.size} ligne(s) sélectionnée(s)</span>
            <Button
              variant="secondary"
              onClick={(): void => {
                setPreview(null);
                setChecked(new Set());
              }}
            >
              Annuler
            </Button>
            <Button onClick={handleCommit} disabled={commitMut.isPending || checked.size === 0}>
              {commitMut.isPending ? 'Import…' : `Importer ${String(checked.size)} contact(s)`}
            </Button>
          </div>
        </section>
      )}
    </div>
  );
}
